/* eslint-disable @typescript-eslint/no-explicit-any */
import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import { Modal } from "@/src/components/Modal/Modal";
import { Label, textVariants } from "@/src/components/Text/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/shad/select";
import useUrlState from "@/src/hooks/use-url-state";
import api from "@/src/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import gallery from "@assets/Gallery.svg";
import { isEmpty } from "lodash";
import { XIcon } from "lucide-react";

interface Props {
  open: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setOpen: (val: any) => void;
  reasons: string[] | undefined;
}

const RaiseDispute = ({ open, setOpen, reasons }: Props) => {
  const { id } = useParams();
  const [reason, setReason] = useState("");
  const [desc, setdesc] = useState("");
  const [, setModal] = useUrlState("modal");
  const [files, setFiles] = useState<Array<string>>([]);
  const queryClient = useQueryClient();

  const fileRef = useRef<any>(null);

  async function handleAction(images: any) {
    const res = await api.post(`${apiRoutes.trade}${id}/`, {
      type: "raise_dispute",
      description: desc,
      reason: reason,
      images: images,
    });
    return res.data;
  }

  const { mutate, isLoading } = useMutation({
    mutationFn: handleAction,
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data?.info.message);
        setModal(undefined);
        queryClient.invalidateQueries(["get_trade_info"]);
      }
    },
  });

  // function to create base64 string
  const readAndCreate = (file: File) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onloadend = () => {
      const base64String = fileReader.result?.toString().split(",")[1];
      setFiles((prev) => [...prev, base64String as string]);
    };
  };

  // function to handle file change
  function handleFileChange(e: any) {
    const files = e.target.files;
    // Use the files as an array and call the readAndCreate function on each file mapped
    Array.prototype.forEach.call(files, readAndCreate);
  }

  // Function to remove images
  function removeImage(image: string) {
    const filteredImages = files.filter((item) => item !== image);
    setFiles(filteredImages);
  }

  const imageUploadFn = async () => {
    const res = await api.post(apiRoutes.imageUpload, {
      path: "chat",
      images: files,
    });
    return res.data;
  };

  const uploadMutation = useMutation({
    mutationFn: imageUploadFn,
    onSuccess: (data) => {
      if (data?.success) {
        mutate(data?.info);
      }
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Raise Dispute"
      setter="url"
      footer={
        <Button
          onClick={() => uploadMutation.mutate()}
          loading={isLoading || uploadMutation.isLoading}
        >
          Raise Dispute
        </Button>
      }
    >
      <div>
        <span className={textVariants({ variant: "desc", size: "xs" })}>
          Dispute and supporting evidence are accessible to both parties and the
          administrator. Please refrain from including any confidential or
          sensitive information.
        </span>
        <div className="mt-4 grid gap-3">
          <div>
            <Label className="text-base">Reason</Label>
            <Select onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="" />
              </SelectTrigger>
              <SelectContent>
                {reasons?.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-base">Description</Label>
            <textarea
              onChange={(e) => setdesc(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-bg-input-field dark:bg-grey-600 dark:text-white  px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 "
              placeholder="Please provide more details about the dispute"
              rows={10}
            />
          </div>
        </div>
        <div className="grid mt-5">
          <Label className="text-base">Upload Proof</Label>
          <span className={textVariants({ size: "xs", variant: "desc" })}>
            The proof can include Screenshots, Video Recording and documents of
            payment
          </span>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-5">
            <div className="bg-[#F6F8FF] rounded-lg p-4 flex flex-col items-center">
              <img src={gallery} alt="gallery" className="h-6 w-6" />
              <span className={textVariants({ size: "xs", variant: "desc" })}>
                Upload Product image
              </span>
              <Button
                onClick={() => fileRef.current.click()}
                className="py-1 px-2 text-xs mt-2"
              >
                Upload
              </Button>
              <input
                ref={fileRef}
                className="hidden"
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
              />
            </div>
            {!isEmpty(files) &&
              files.map((image, key) => (
                <div className="relative" key={key}>
                  <span
                    onClick={() => removeImage(image)}
                    className="absolute top-0 right-0 rounded-full p-1 bg-gray-300 dark:bg-grey-500"
                  >
                    <XIcon className="h-4 w-4 dark:text-gray-200 text-gray-500 cursor-pointer" />
                  </span>
                  <img
                    src={`data:image/jpeg;base64,${image}`}
                    className="h-20 w-full object-contain"
                    alt="image"
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RaiseDispute;
